import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const API_URL = 'https://chatbot-worker.ebraam-saad.workers.dev';

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [view, setView] = useState('login');
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const messagesEndRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auth forms state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // MCP form state
  const [mcpName, setMcpName] = useState('');
  const [mcpUrl, setMcpUrl] = useState('');
  const [mcpApiKey, setMcpApiKey] = useState('');

  // Safely parse fetch responses that might not be JSON or might have empty bodies
  const parseResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    const raw = await response.text();
    let data = null;
    if (raw && contentType.includes('application/json')) {
      try {
        data = JSON.parse(raw);
      } catch (_) {
        // Fallback if JSON parse fails
        data = { error: raw };
      }
    } else if (raw) {
      data = { error: raw };
    } else {
      data = { error: `${response.status} ${response.statusText}` };
    }
    return data;
  };

  useEffect(() => {
    if (token) {
      fetchConversations();
      fetchMCPServers();
    }
  }, [token]);

  // Handle magic link callback: capture access token from URL on first load
  useEffect(() => {
    const url = new URL(window.location.href);
    // Supabase may put tokens in hash or query depending on flow
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const queryParams = url.searchParams;
    const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
    if (accessToken) {
      setToken(accessToken);
      localStorage.setItem('token', accessToken);
      setView('chat');
      // Optional: clean token from URL
      window.history.replaceState({}, document.title, url.pathname);
    }
  }, []);

  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation.id);
    }
  }, [currentConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await parseResponse(response);
      if (response.ok) {
        // In email confirmation flow, session may be null; show guidance
        if (data && data.session) {
          setToken(data.session.access_token);
          localStorage.setItem('token', data.session.access_token);
          setUser(data.user);
          setView('chat');
        } else {
          alert('Check your email to confirm your account.');
          setView('login');
        }
      } else {
        alert(data?.error || 'Check your email and click on the link, and then come back here and click on Login');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await parseResponse(response);
      if (response.ok && data.session) {
        setToken(data.session.access_token);
        localStorage.setItem('token', data.session.access_token);
        setUser(data.user);
        setView('chat');
      } else {
        alert(data?.error || 'Login failed');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    setView('login');
    setConversations([]);
    setCurrentConversation(null);
    setMessages([]);
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      const data = await response.json().catch(() => []);
      const list = Array.isArray(data) ? data : [];
      setConversations(list);
      if (list.length > 0 && !currentConversation) {
        setCurrentConversation(list[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const createNewConversation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const data = await parseResponse(response);
        throw new Error(data?.error || 'Failed to create conversation');
      }
      const data = await response.json();
      setConversations([data, ...conversations]);
      setCurrentConversation(data);
      setMessages([]);
    } catch (error) {
      alert('Error creating conversation: ' + error.message);
    }
    setLoading(false);
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Be robust to non-array or error responses
      const data = await response.json().catch(() => null);
      const next = Array.isArray(data) ? data : [];
      setMessages(next);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    if (!currentConversation) {
      alert('No conversation selected.');
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      created_at: new Date().toISOString()
    };
    
    setMessages([...messages, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conversationId: currentConversation.id,
          message: inputMessage
        })
      });
      if (!response.ok) {
        const data = await parseResponse(response);
        throw new Error(data?.error || 'Chat request failed');
      }
      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        created_at: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      alert('Error sending message: ' + error.message);
    }
    setLoading(false);
  };

  const fetchMCPServers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mcp-servers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMcpServers(data);
    } catch (error) {
      console.error('Error fetching MCP servers:', error);
    }
  };

  const addMCPServer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/mcp-servers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: mcpName,
          url: mcpUrl,
          api_key: mcpApiKey || null
        })
      });
      const data = await response.json();
      setMcpServers([...mcpServers, data]);
      setMcpName('');
      setMcpUrl('');
      setMcpApiKey('');
      alert('MCP Server added successfully!');
    } catch (error) {
      alert('Error adding MCP server: ' + error.message);
    }
    setLoading(false);
  };

  const deleteMCPServer = async (serverId) => {
    if (!window.confirm('Delete this MCP server?')) return;
    
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/mcp-servers/${serverId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMcpServers(mcpServers.filter(s => s.id !== serverId));
    } catch (error) {
      alert('Error deleting MCP server: ' + error.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="app">
        <div className="auth-container">
          <h1>Chatbot</h1>
          <div className="auth-tabs">
            <button 
              className={view === 'login' ? 'active' : ''}
              onClick={() => setView('login')}
            >
              Login
            </button>
            <button 
              className={view === 'signup' ? 'active' : ''}
              onClick={() => setView('signup')}
            >
              Sign Up
            </button>
          </div>
          
          {view === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Loading...' : 'Login'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password (min 6 chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Loading...' : 'Sign Up'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
    {/* Bottone per aprire/chiudere la sidebar su mobile */}
    <button
      className="menu-toggle"
      onClick={() => setSidebarOpen(!sidebarOpen)}
    >
      ☰
    </button>

    {/* Overlay scuro dietro la sidebar */}
    {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

    {/* Sidebar */}
    <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
      <div className="sidebar-header">
        <h2>Conversations</h2>
        <button onClick={createNewConversation} disabled={loading}>
          + New
        </button>
      </div>
      <div className="conversations-list">
        {conversations.map(conv => (
          <div
            key={conv.id}
            className={`conversation-item ${currentConversation?.id === conv.id ? 'active' : ''}`}
            onClick={() => {
              setCurrentConversation(conv);
              setSidebarOpen(false); // chiude sidebar su mobile
            }}
          >
            <span>Chat {new Date(conv.created_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
        
        <div className="sidebar-section">
          <h3>MCP Servers</h3>
          <button onClick={() => setView(view === 'mcp' ? 'chat' : 'mcp')}>
            {view === 'mcp' ? 'Hide' : 'Manage'}
          </button>
        </div>
        
        <div className="sidebar-footer">
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="main-content">
        {view === 'mcp' ? (
          <div className="mcp-manager">
            <h2>MCP Servers</h2>
            <form onSubmit={addMCPServer} className="mcp-form">
              <input
                type="text"
                placeholder="Server Name"
                value={mcpName}
                onChange={(e) => setMcpName(e.target.value)}
                required
              />
              <input
                type="url"
                placeholder="Server URL"
                value={mcpUrl}
                onChange={(e) => setMcpUrl(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="API Key (optional)"
                value={mcpApiKey}
                onChange={(e) => setMcpApiKey(e.target.value)}
              />
              <button type="submit" disabled={loading}>
                Add Server
              </button>
            </form>
            
            <div className="mcp-list">
              {mcpServers.map(server => (
                <div key={server.id} className="mcp-item">
                  <div>
                    <strong>{server.name}</strong>
                    <p>{server.url}</p>
                    <span className={server.enabled ? 'enabled' : 'disabled'}>
                      {server.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <button onClick={() => deleteMCPServer(server.id)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="chat-container">
            <div className="chat-header">
              <h2>Chat</h2>
              {mcpServers.length > 0 && (
                <span className="tools-badge">
                  {mcpServers.filter(s => s.enabled).length} tools connected
                </span>
              )}
            </div>
            
            <div className="messages-container">
              {messages.map((msg, idx) => (
                <div key={idx} className={`message ${msg.role}`}>
                  <div className="message-role">
                    {msg.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="message-content">
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="message assistant">
                  <div className="message-role">🤖</div>
                  <div className="message-content typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={sendMessage} className="message-input">
              <input
                type="text"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                disabled={loading || !currentConversation}
              />
              <button type="submit" disabled={loading || !currentConversation || !inputMessage.trim()}>
                Send
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;